import { useMatch } from 'react-router-dom';
import { useAppSelector } from '@/app/hooks';
import { selectTableSession, type TableSession } from '@/features/tableSession/tableSessionSlice';

/**
 * SC-692 — cette table a-t-elle ete ouverte depuis le mode serveur ? C'est le
 * meme drapeau qui decide de la WaiterBar dans RestaurantShell : une session
 * `waiterMode` est le carnet du serveur, une session issue d'un QR scanne est
 * le telephone du client.
 */
export function isWaiterTableSession(
  session: TableSession | null,
  restaurantUuid: string,
): boolean {
  return session?.restaurantUuid === restaurantUuid && session.waiterMode === true;
}

/**
 * SC-692 — « cet écran est-il celui du personnel ? »
 *
 * Le mode serveur et le parcours client vivent dans la MEME application, sous
 * les mêmes URLs (`/:restaurantUuid/table/:token` sert la prise de commande du
 * serveur comme le QR scanné par le client). La seule présence d'un jeton
 * serveur ne dit donc rien : il survit dans localStorage, et un client qui
 * scanne sur le même appareil réveillait les outils de salle sur son écran.
 *
 * Deux surfaces — et deux seulement — appartiennent au personnel :
 *   1. tout ce qui vit sous `/:restaurantUuid/serveur` — la grille des notes
 *      ET, depuis le carnet du serveur (spec 2026-08-27), l'écran de note
 *      `/:restaurantUuid/serveur/ticket/:sessionUuid` ;
 *   2. une table OUVERTE depuis cette grille (`session.waiterMode`, posé par
 *      WaiterShell et déjà ce qui décide de la WaiterBar dans RestaurantShell).
 *
 * Tout le reste — QR scanné, carte, panier, paiement — est le client.
 */
export function useIsWaiterSurface(restaurantUuid: string): boolean {
  const session = useAppSelector(selectTableSession);
  // Trailing `/*` matches both the bare `/serveur` list AND any sub-route
  // (`/serveur/ticket/<uuid>`) — verified against react-router-dom's
  // matchPath: a splat pattern matches its own base path too.
  const waiterRoutes = useMatch('/:restaurantUuid/serveur/*');

  const onWaiterRoutes = waiterRoutes?.params.restaurantUuid === restaurantUuid;

  return onWaiterRoutes || isWaiterTableSession(session, restaurantUuid);
}
